/*
 * Reports a genuine client-side fetch failure (network error / non-2xx
 * response) to New Relic, so failed API calls made from product cards are
 * visible without relying on users reporting problems.
 *
 * Not intended for business-level error responses (e.g. a 200 response body
 * containing an `errors` array) — those are already surfaced to the user via
 * existing UI events and shouldn't be double-reported here.
 */
export type ClientErrorContext = {
  /** Identifies which API/feature the failed fetch belongs to. */
  api: string;
  consumerId: string;
  config: string | undefined;
  [key: string]: unknown;
};

const ERROR_PREFIX = "pcl-fetch-error";

const isNewRelicEnabled = () => {
  return typeof window?.newrelic?.setCustomAttribute === "function";
};

export function reportClientError(
  error: unknown,
  context: ClientErrorContext,
): void {
  if (typeof window === "undefined") {
    return;
  }

  if (!isNewRelicEnabled()) {
    return;
  }

  const originalMessage =
    error instanceof Error ? error.message : String(error);
  const noticeErrorError = new Error(
    `${ERROR_PREFIX}: ${context.api}: ${originalMessage}`,
  );
  if (error instanceof Error && error.stack) {
    noticeErrorError.stack = error.stack;
  }

  const attributes = Object.fromEntries(
    Object.entries(context)
      .filter(([, value]) => value !== undefined)
      .map(([key, value]) => [key, String(value)]),
  );

  window.newrelic.noticeError(noticeErrorError, attributes);
}
